import React, { useState, useEffect } from "react";
// Ícones
import {
  X, Trash2, ShoppingCart, Plus, Minus, ArrowLeft, Truck, Loader2,
  CreditCard, Banknote, QrCode
} from "lucide-react";

const CartSidebar = ({ cartOpen, cartItems, onClose, onRemove, onIncrease, onDecrease }) => {
  // --- ESTADOS DO CARRINHO ---
  
  const [step, setStep] = useState('cart');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [cep, setCep] = useState('');
  const [address, setAddress] = useState(null);
  const [shippingCost, setShippingCost] = useState(null);
  const [cepError, setCepError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // --- CÁLCULOS DE VALOR ---

  const itemTotal = cartItems.reduce((total, item) => {
    const price = parseFloat(String(item.price).replace(",", "."));
    return total + (price * item.quantity);
  }, 0);

  // O '|| 0' garante que o total funcione mesmo se o frete for null
  const grandTotal = itemTotal + (shippingCost || 0);

  // --- EFEITOS ---

  useEffect(() => {
    if (!cartOpen) {
      setTimeout(() => {
        setStep('cart');
        setCep('');
        setAddress(null);
        setShippingCost(null);
        setCepError('');
        setSelectedPaymentMethod(null);
      }, 300);
    }
  }, [cartOpen]);

  // --- LÓGICA DE FRETE ---

  const handleCepSearch = async () => {
    setIsLoading(true);
    setCepError('');
    setAddress(null);
    setShippingCost(null);

    const formattedCep = cep.replace(/\D/g, '');

    if (formattedCep.length !== 8) {
      setCepError('CEP inválido. Deve conter 8 números.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`https://viacep.com.br/ws/${formattedCep}/json/`);
      const data = await response.json();

      if (data.erro) {
        setCepError('CEP não encontrado.');
      } else {
        const allowedStates = ['DF', 'GO'];
        if (allowedStates.includes(data.uf)) {
          setAddress(data);
          calculateShipping(data); // Calcula o frete
          setCepError('');
        } else {
          setCepError(`Desculpe, no momento não entregamos para a região de ${data.localidade} - ${data.uf}.`);
        }
      }
    } catch (error) {
      setCepError('Erro ao buscar o CEP. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Calcula o custo do frete baseado em FAIXAS DE CEP.
   */
  const calculateShipping = (addressData) => {
    const cepNumerico = parseInt(addressData.cep.replace(/\D/g, ''), 10);
    
    // --- ALTERAÇÃO AQUI ---
    // CEP de Brasília e arredores agora é Frete Grátis (0)
    if (cepNumerico >= 70000000 && cepNumerico <= 72999999) {
      setShippingCost(0); // <-- MUDADO DE 10.00 PARA 0
    } else if (cepNumerico >= 73000000 && cepNumerico <= 73699999) { // Cidades satélites DF
      setShippingCost(15.00);
    } else if (cepNumerico >= 74000000 && cepNumerico <= 76799999) { // Goiânia e região
      setShippingCost(25.00);
    } else if (cepNumerico >= 73700000 && cepNumerico <= 76999999) { // Restante de Goiás
      setShippingCost(35.00);
    } else {
      setShippingCost(null);
      setCepError('Não foi possível calcular o frete para este CEP.');
    }
  };

  // --- LÓGICA DE FINALIZAÇÃO (WHATSAPP) ---

  const handleFinalizeOrder = () => {
    const phoneNumber = '+5561981531285'; // <-- Lembre-se de trocar seu número aqui

    const itemsList = cartItems.map(item => 
      `  - ${item.quantity}x ${item.title} (R$ ${item.price}) - Subtotal: R$ ${(item.quantity * parseFloat(String(item.price).replace(",", "."))).toFixed(2)}`
    ).join('\n');

    const addressDetails = 
`*Endereço de Entrega:*
  ${address.logradouro}, ${address.bairro}
  ${address.localidade} - ${address.uf}
  CEP: ${address.cep}`;

    // --- ALTERAÇÃO AQUI ---
    // Formata o frete para a mensagem do WhatsApp
    const freteFormatado = shippingCost === 0 ? 'Grátis' : (shippingCost !== null ? `R$ ${shippingCost.toFixed(2)}` : 'A calcular');
    
    const summary = 
`*Resumo do Pedido:*
  Subtotal: R$ ${itemTotal.toFixed(2)}
  Frete: ${freteFormatado}
  *Total: R$ ${grandTotal.toFixed(2)}*`;

    const payment = `*Forma de Pagamento:* ${selectedPaymentMethod}`;

    const message = `Olá InsetoBras! 👋\nGostaria de finalizar o meu pedido:\n\n*Itens:*
${itemsList}
\n${addressDetails}
\n${summary}
\n${payment}
\nObrigado!`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
    onClose();
  };


  // --- COMPONENTE HELPER PARA OS BOTÕES DE PAGAMENTO ---
  const PaymentButton = ({ icon, text, onClick, selected }) => (
    <button
      onClick={onClick}
      className={`flex items-center w-full p-4 rounded-lg border-2 transition-all ${
        selected
          ? 'border-green-600 bg-green-50 shadow-md'
          : 'border-gray-300 bg-white hover:bg-gray-50'
      }`}
    >
      {React.cloneElement(icon, { className: "w-6 h-6 mr-3 text-green-700" })}
      <span className="font-semibold">{text}</span>
    </button>
  );


  // --- RENDERIZAÇÃO ---
  return (
    <div
      className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-in-out
        ${cartOpen ? 'bg-opacity-50' : 'bg-opacity-0 pointer-events-none'}`
      }
      onClick={onClose}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        className={`fixed top-0 right-0 bg-gray-50 w-full max-w-sm h-full shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out
          ${cartOpen ? 'translate-x-0' : 'translate-x-full'}`
        }
      >
        {/* Cabeçalho dinâmico */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {step !== 'cart' && (
            <button 
              onClick={() => setStep(step === 'shipping' ? 'cart' : 'shipping')} 
              className="p-2 rounded-full hover:bg-gray-200"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
          )}
          <h2 className="text-xl font-semibold text-gray-800">
            {step === 'cart' && 'Meu Carrinho'}
            {step === 'shipping' && 'Endereço de Entrega'}
            {step === 'payment' && 'Forma de Pagamento'}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* --- Container das 3 Etapas --- */}
        <div className="flex-grow overflow-x-hidden">
          <div 
            className="flex h-full transition-transform duration-300 ease-in-out"
            style={{ 
              transform: step === 'cart' ? 'translateX(0%)' : (step === 'shipping' ? 'translateX(-100%)' : 'translateX(-200%)')
            }}
          >
            {/* --- ETAPA 1: Itens do Carrinho --- */}
            <div className="w-full flex-shrink-0 flex flex-col h-full">
              {cartItems.length === 0 ? (
                <div className="flex-grow flex flex-col justify-center items-center text-gray-500 p-4">
                  <ShoppingCart className="w-16 h-16 mb-4" />
                  <h3 className="text-lg font-semibold">Seu carrinho está vazio</h3>
                </div>
              ) : (
                <ul className="flex-grow overflow-y-auto p-4 space-y-4">
                  {cartItems.map((product) => (
                    <li key={product.title} className="flex gap-4 bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                      <img src={product.image} alt={product.title} className="w-20 h-20 object-cover rounded-md" />
                      <div className="flex-grow flex flex-col">
                        <div className="flex justify-between items-start">
                          <p className="font-semibold text-gray-800 flex-grow pr-2">{product.title}</p>
                          <button onClick={() => onRemove(product.title)} className="p-1 rounded-full hover:bg-red-100 text-red-500"><Trash2 className="w-5 h-5" /></button>
                        </div>
                        <div className="flex items-center justify-between mt-auto">
                          <div className="flex items-center gap-2 border border-gray-300 rounded">
                            <button onClick={() => onDecrease(product.title)} className="px-2 py-1 hover:bg-gray-200"><Minus className="w-4 h-4" /></button>
                            <span className="px-2 font-semibold">{product.quantity}</span>
                            <button onClick={() => onIncrease(product.title)} className="px-2 py-1 hover:bg-gray-200"><Plus className="w-4 h-4" /></button>
                          </div>
                          <p className="font-bold text-gray-800">R$ {product.price}</p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              {cartItems.length > 0 && (
                <div className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex justify-between items-center mb-4 text-lg">
                    <span className="font-semibold text-gray-800">Subtotal:</span>
                    <span className="font-bold text-green-700">R$ {itemTotal.toFixed(2)}</span>
                  </div>
                  <button 
                    onClick={() => setStep('shipping')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition-colors"
                  >
                    Finalizar Compra
                  </button>
                </div>
              )}
            </div>

            {/* --- ETAPA 2: Frete e Endereço --- */}
            <div className="w-full flex-shrink-0 flex flex-col h-full p-4 space-y-4 overflow-y-auto">
              <h3 className="font-semibold text-gray-700">Calcular Frete</h3>
              <p className="text-sm text-gray-500">Entregamos apenas para **Distrito Federal (DF)** e **Goiás (GO)**.</p>
              
              <div className="flex gap-2">
                <input type="tel" value={cep} onChange={(e) => setCep(e.target.value)} placeholder="Digite seu CEP" className="flex-grow p-2 border border-gray-300 rounded-md" />
                <button onClick={handleCepSearch} disabled={isLoading} className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:bg-gray-400">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'OK'}
                </button>
              </div>

              {cepError && (<p className="text-sm text-red-600">{cepError}</p>)}
              
              {address && (
                <div className="p-3 bg-white rounded-md border border-gray-200 space-y-2">
                  <h4 className="font-semibold">Endereço:</h4>
                  <p className="text-sm text-gray-700">{address.logradouro}, {address.bairro}<br />{address.localidade} - {address.uf}<br />CEP: {address.cep}</p>
                  
                  {/* --- ALTERAÇÃO AQUI --- */}
                  {/* Mostra "Grátis" se o custo for 0 */}
                  {shippingCost !== null && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Truck className="w-5 h-5 text-green-600"/>
                      <span className={`text-sm font-semibold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                        Valor do Frete: {shippingCost === 0 ? 'Grátis' : `R$ ${shippingCost.toFixed(2)}`}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-auto pt-4 border-t border-gray-200 bg-gray-50">
                <div className="space-y-2 mb-4 text-md">
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal:</span><span className="font-semibold text-gray-800">R$ {itemTotal.toFixed(2)}</span></div>
                  
                  {/* --- ALTERAÇÃO AQUI --- */}
                  {/* Mostra "Grátis" no resumo também */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frete:</span>
                    <span className={`font-semibold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                      {shippingCost !== null ? (shippingCost === 0 ? 'Grátis' : `R$ ${shippingCost.toFixed(2)}`) : 'A calcular'}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-lg"><span className="font-bold text-gray-800">Total:</span><span className="font-bold text-green-700">R$ {grandTotal.toFixed(2)}</span></div>
                </div>
                <button 
                  onClick={() => setStep('payment')}
                  disabled={shippingCost === null}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Continuar para Pagamento
                </button>
              </div>
            </div>

            {/* --- ETAPA 3: Pagamento --- */}
            <div className="w-full flex-shrink-0 flex flex-col h-full p-4 overflow-y-auto">
              <h3 className="font-semibold text-gray-700 mb-4">Escolha a Forma de Pagamento</h3>
              
              <div className="space-y-3">
                <PaymentButton icon={<QrCode />} text="Pix" onClick={() => setSelectedPaymentMethod('Pix')} selected={selectedPaymentMethod === 'Pix'} />
                <PaymentButton icon={<CreditCard />} text="Cartão de Crédito" onClick={() => setSelectedPaymentMethod('Crédito')} selected={selectedPaymentMethod === 'Crédito'} />
                <PaymentButton icon={<Banknote />} text="Cartão de Débito" onClick={() => setSelectedPaymentMethod('Débito')} selected={selectedPaymentMethod === 'Débito'} />
              </div>
              
              <div className="mt-auto pt-4 border-t border-gray-200 bg-gray-50">
                <div className="space-y-2 mb-4 text-md">
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal:</span><span className="font-semibold text-gray-800">R$ {itemTotal.toFixed(2)}</span></div>
                  
                  {/* --- ALTERAÇÃO AQUI --- */}
                  {/* Mostra "Grátis" no resumo final */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frete:</span>
                    <span className={`font-semibold ${shippingCost === 0 ? 'text-green-600' : 'text-gray-800'}`}>
                      {shippingCost !== null ? (shippingCost === 0 ? 'Grátis' : `R$ ${shippingCost.toFixed(2)}`) : 'A calcular'}
                    </span>
                  </div>

                  <div className="flex justify-between text-lg"><span className="font-bold text-gray-800">Total:</span><span className="font-bold text-green-700">R$ {grandTotal.toFixed(2)}</span></div>
                </div>
                <button 
                  onClick={handleFinalizeOrder}
                  disabled={!selectedPaymentMethod}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Finalizar e Enviar Pedido
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CartSidebar;

